import { useCallback, useMemo, useSyncExternalStore } from "react";
import { CombinedGraphQLErrors, type ApolloClient } from "@apollo/client";
import { useApolloClient, useQuery } from "@apollo/client/react";
import { toastManager } from "@salestrak/ui";

import {
  CreateFormDocument,
  DeleteFormDocument,
  DeleteFormSubmissionDocument,
  DeleteFormSubmissionPhotosDocument,
  DiscardFormDraftDocument,
  DuplicateFormDocument,
  FormFieldsFragmentDoc,
  FormsDocument,
  IssueFormSubmissionsDocument,
  PublishFormDocument,
  ReopenFormDocument,
  RestoreFormSubmissionDocument,
  SaveFormDraftDocument,
  SaveFormSubmissionDocument,
  SetFormSharesDocument,
  SetFormSubmissionSharesDocument,
  SubmitFormSubmissionDocument,
  UnpublishFormDocument,
  UpdateFormSettingsDocument,
  UpdateFormDocument,
  type FormRow,
} from "#app/lib/data";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import {
  editableShape,
  formFromRow,
  rootSeatAt,
  shapeForSave,
  submissionFromRow,
  withConditionReleased,
  withItemRelinked,
  withItemRemoved,
  withItemReplaced,
  withRunMoved,
  withSectionMoved,
  reorderWithRun,
  isFieldItem,
  resolveItemField,
  type Form,
  type FormFieldItem,
  type FormItem,
  type FormSettings,
  type FormShape,
  type FormShare,
  type FormSubmission,
} from "./model";
import type { CustomFieldDef } from "#app/components/custom-fields/types";

const SAVE_DELAY_MS = 400;
const ANSWERS_DELAY_MS = 500;
const ANSWERS_RETRY_MS = 10_000;
const SAVE_RETRY_MS = 10_000;
const FILLING_QUERIES = ["IssueFormSubmissions"];

type Failure = { ok: false; code: string; message: string };
export type FormOutcome = { ok: true; form: Form } | Failure;

function failure(result: { __typename?: string; code?: string; message?: string } | null | undefined): Failure {
  return {
    ok: false,
    code: result?.code ?? "FAILED",
    message: result?.message ?? "Something went wrong",
  };
}

function formOutcome(result: { __typename?: string } | null | undefined): FormOutcome {
  return result?.__typename === "Form" ? { form: formFromRow(result as FormRow), ok: true } : failure(result);
}

// Read synchronously: an edit must show in the same event that made it, and the cache broadcasts later.
const overlays = new Map<string, FormShape>();
const settingsOverlays = new Map<string, FormSettings>();
const overlayListeners = new Set<() => void>();
let overlayStamp = 0;

function overlaysChanged() {
  overlayStamp += 1;
  overlayListeners.forEach((listener) => listener());
}

function setOverlay(formId: string, shape: FormShape | null) {
  if (shape) overlays.set(formId, shape);
  else if (!overlays.delete(formId)) return;
  overlaysChanged();
}

function setSettingsOverlay(formId: string, settings: FormSettings | null) {
  if (settings) settingsOverlays.set(formId, settings);
  else if (!settingsOverlays.delete(formId)) return;
  overlaysChanged();
}

function subscribeOverlays(listener: () => void) {
  overlayListeners.add(listener);
  return () => overlayListeners.delete(listener);
}

function withOverlay(form: Form): Form {
  const shape = overlays.get(form.formId);
  const settings = settingsOverlays.get(form.formId);
  const shaped = !shape ? form : form.status === "draft" ? { ...form, items: shape.items } : { ...form, draft: shape };
  return settings ? { ...shaped, settings } : shaped;
}

type ChainEntry<T> = { client: ApolloClient; pending: T | null; inFlight: Promise<boolean> | null; timer: ReturnType<typeof setTimeout> | null };

// One chain per key, module-wide. A write returning false did not land: its value stays staged until the next change or flush, and "retry" also arms `retryMs`.
function saveChain<T>(options: {
  write: (client: ApolloClient, key: string, value: T) => Promise<boolean | "retry" | void>;
  settled: (key: string) => void;
  delayMs: number;
  retryMs?: number;
}) {
  const entries = new Map<string, ChainEntry<T>>();

  function flush(key: string): Promise<boolean> {
    const entry = entries.get(key);
    if (entry?.timer) {
      clearTimeout(entry.timer);
      entry.timer = null;
    }
    return drain(key);
  }

  function drain(key: string): Promise<boolean> {
    const entry = entries.get(key);
    if (!entry) return Promise.resolve(true);
    if (entry.inFlight) return entry.inFlight;
    const value = entry.pending;
    if (!value) {
      if (!entry.timer) {
        entries.delete(key);
        options.settled(key);
      }
      return Promise.resolve(true);
    }
    entry.pending = null;
    entry.inFlight = options.write(entry.client, key, value).then((landed) => {
      entry.inFlight = null;
      if (landed !== false && landed !== "retry") return drain(key);
      entry.pending ??= value;
      if (landed === "retry" && options.retryMs && !entry.timer) {
        entry.timer = setTimeout(() => {
          entry.timer = null;
          void drain(key);
        }, options.retryMs);
      }
      return false;
    });
    return entry.inFlight;
  }

  return {
    drop(key: string) {
      const entry = entries.get(key);
      if (!entry) return;
      if (entry.timer) clearTimeout(entry.timer);
      entries.delete(key);
      void Promise.resolve(entry.inFlight).then(() => options.settled(key));
    },
    reset() {
      for (const entry of entries.values()) if (entry.timer) clearTimeout(entry.timer);
      entries.clear();
    },
    async takeBack(key: string): Promise<T | null> {
      const entry = entries.get(key);
      if (!entry) return null;
      if (entry.timer) clearTimeout(entry.timer);
      entry.timer = null;
      const value = entry.pending;
      entry.pending = null;
      await entry.inFlight;
      // A write in flight that failed is staged again for its retry; the replacing write supersedes it too.
      if (entry.timer) clearTimeout(entry.timer);
      entry.timer = null;
      const failed = entry.pending;
      entry.pending = null;
      if (!entry.inFlight && entries.get(key) === entry) entries.delete(key);
      return value ?? failed;
    },
    flush,
    flushAll(): Promise<boolean[]> {
      return Promise.all([...entries.keys()].map(flush));
    },
    stage(client: ApolloClient, key: string, value: T) {
      const entry = entries.get(key) ?? { client, inFlight: null, pending: null, timer: null };
      entries.set(key, entry);
      entry.pending = value;
      if (entry.timer) clearTimeout(entry.timer);
      entry.timer = setTimeout(() => {
        entry.timer = null;
        void drain(key);
      }, options.delayMs);
    },
  };
}

function formCacheId(client: ApolloClient, formId: string): string {
  return client.cache.identify({ __typename: "Form", id: formId }) ?? `Form:${formId}`;
}

function cachedRow(client: ApolloClient, formId: string): FormRow | null {
  return client.cache.readFragment({ fragment: FormFieldsFragmentDoc, fragmentName: "FormFields", id: formCacheId(client, formId) });
}

const shapeVersions = new Map<string, number>();
const failingSaves = new Set<string>();

// A refusal (no access, say) stays refused; only a lost request retries.
function failedSave(key: string, err: unknown, title: string): false | "retry" {
  if (!failingSaves.has(key)) toastMutationError(err, title);
  if (CombinedGraphQLErrors.is(err)) {
    failingSaves.delete(key);
    return false;
  }
  failingSaves.add(key);
  return "retry";
}

const shapeSaves = saveChain<FormShape>({
  delayMs: SAVE_DELAY_MS,
  retryMs: SAVE_RETRY_MS,
  settled: (formId) => {
    shapeVersions.delete(formId);
    setOverlay(formId, null);
  },
  write: async (client, formId, shape) => {
    try {
      const { data } = await client.mutate({
        mutation: SaveFormDraftDocument,
        variables: {
          expectedVersion: shapeVersions.get(formId) ?? cachedRow(client, formId)?.version ?? 0,
          id: formId,
          shape: shapeForSave(shape),
        },
      });
      const result = data?.saveFormDraft;
      failingSaves.delete(`shape:${formId}`);
      if (result?.__typename === "Form") {
        shapeVersions.set(formId, result.version);
        return true;
      }
      toastManager.add({ title: result?.message ?? "Failed to save the form", type: "error" });
      shapeVersions.delete(formId);
      await client.refetchQueries({ include: ["Forms"] });
      // Edits staged on the refused copy would overwrite the newer one under the refetched version, so they go.
      if (result?.code === "STALE_VERSION") shapeSaves.drop(formId);
    } catch (err) {
      return failedSave(`shape:${formId}`, err, "Failed to save the form");
    }
    return false;
  },
});

function stageShape(client: ApolloClient, formId: string, shape: FormShape): void {
  const row = cachedRow(client, formId);
  if (!row) return;
  setOverlay(formId, shape);
  shapeSaves.stage(client, formId, shape);
}

const settingsSaves = saveChain<FormSettings>({
  delayMs: SAVE_DELAY_MS,
  retryMs: SAVE_RETRY_MS,
  settled: (formId) => setSettingsOverlay(formId, null),
  write: async (client, formId, settings) => {
    try {
      const { data } = await client.mutate({ mutation: UpdateFormSettingsDocument, variables: { id: formId, settings } });
      const result = data?.updateFormSettings;
      failingSaves.delete(`settings:${formId}`);
      if (result?.__typename === "Form") return true;
      toastManager.add({ title: result?.message ?? "Failed to save the settings", type: "error" });
    } catch (err) {
      return failedSave(`settings:${formId}`, err, "Failed to save the settings");
    }
    return false;
  },
});

function stageSettings(client: ApolloClient, formId: string, settings: FormSettings): void {
  setSettingsOverlay(formId, settings);
  settingsSaves.stage(client, formId, settings);
}

async function flushFormSaves(client: ApolloClient, formId: string): Promise<number | null | false> {
  const landed = await Promise.all([shapeSaves.flush(formId), settingsSaves.flush(formId)]);
  if (landed.includes(false)) return false;
  return cachedRow(client, formId)?.version ?? null;
}

const SETTLE_TIMEOUT_MS = 3_000;

// Saves read the active workspace and token when they send, so they land before either changes and nothing carries over.
export async function settleFormSaves(): Promise<void> {
  const flushed = Promise.all([shapeSaves.flushAll(), settingsSaves.flushAll(), answerSaves.flushAll()]);
  await Promise.race([flushed, new Promise((resolve) => setTimeout(resolve, SETTLE_TIMEOUT_MS))]);
  resetFormSaves();
}

export function resetFormSaves(): void {
  shapeSaves.reset();
  settingsSaves.reset();
  answerSaves.reset();
  failingSaves.clear();
  shapeVersions.clear();
  overlays.clear();
  settingsOverlays.clear();
}

function fillingInput(filling: FormSubmission, changedItemIds: readonly string[] | null = null) {
  return {
    answers: filling.answers,
    changedItemIds: changedItemIds ? [...changedItemIds] : null,
    formId: filling.formId,
    formVersionId: filling.formVersionId,
    id: filling.submissionId,
    issueId: filling.issueId,
    prefilled: filling.prefilled ?? null,
  };
}

function fillingListed(client: ApolloClient, filling: FormSubmission): boolean {
  const data = client.readQuery({ query: IssueFormSubmissionsDocument, variables: { issueId: filling.issueId } });
  return !!data?.issue?.formSubmissions.some((row) => row.id === filling.submissionId);
}

type StagedAnswers = {
  filling: FormSubmission;
  changed: readonly string[] | null;
  onSaved: ((stored: Record<string, unknown>) => void) | null;
  onRefused: ((message: string) => void) | null;
};

// One chain per filling, so two saves never race: the first stores the row, the next lands on it.
const answerSaves = saveChain<StagedAnswers>({
  delayMs: ANSWERS_DELAY_MS,
  retryMs: ANSWERS_RETRY_MS,
  settled: () => {},
  write: async (client, submissionId, { filling, changed, onSaved, onRefused }) => {
    try {
      const listed = fillingListed(client, filling);
      const { data } = await client.mutate({
        mutation: SaveFormSubmissionDocument,
        variables: { input: fillingInput(filling, changed) },
      });
      const result = data?.saveFormSubmission;
      if (!listed || !result) void client.refetchQueries({ include: FILLING_QUERIES });
      failingSaves.delete(submissionId);
      if (result && result.__typename !== "FormSubmission") {
        const message = result.message ?? "Failed to save answers";
        if (onRefused) onRefused(message);
        else toastManager.add({ title: message, type: "error" });
        return;
      }
      onSaved?.(result ? ((result.answers ?? {}) as Record<string, unknown>) : filling.answers);
    } catch (err) {
      if (failedSave(submissionId, err, "Failed to save answers") === "retry") return "retry";
    }
  },
});

const decodedForms = new WeakMap<FormRow, Form>();

function decodedForm(row: FormRow): Form {
  const known = decodedForms.get(row);
  if (known) return known;
  const form = formFromRow(row);
  decodedForms.set(row, form);
  return form;
}

export function useSubmissionOpenable(issueId: string | undefined, submissionId: string | undefined): boolean {
  const variables = useMemo(() => ({ issueId: issueId ?? "" }), [issueId]);
  const { data } = useQuery(IssueFormSubmissionsDocument, { skip: !issueId, variables });
  const rows = data?.issue?.formSubmissions;
  if (!rows) return false;
  return rows.some((row) => row.id === submissionId && row.viewerCanOpen);
}

export function useRemoveFormPhotos() {
  const client = useApolloClient();
  return useCallback(
    async (targetIssueId: string, photoIds: readonly string[]): Promise<number> => {
      try {
        const { data } = await client.mutate({
          mutation: DeleteFormSubmissionPhotosDocument,
          refetchQueries: [...FILLING_QUERIES, "IssueViewData"],
          variables: { issueId: targetIssueId, mediaIds: [...photoIds] },
        });
        const deleted = new Set(data?.deleteFormSubmissionPhotos ?? []);
        return photoIds.filter((id) => !deleted.has(id)).length;
      } catch (err) {
        toastMutationError(err, "Failed to remove photo");
        return photoIds.length;
      }
    },
    [client],
  );
}

export function useFormsStore(issueId?: string | null, { skip = false }: { skip?: boolean } = {}) {
  const client = useApolloClient();
  const formsQuery = useQuery(FormsDocument, { skip });
  const fillingVariables = useMemo(() => ({ issueId: issueId ?? "" }), [issueId]);
  const fillingsQuery = useQuery(IssueFormSubmissionsDocument, { skip: skip || !issueId, variables: fillingVariables });

  const stamp = useSyncExternalStore(subscribeOverlays, () => overlayStamp);
  const forms = useMemo(() => {
    void stamp;
    return (formsQuery.data?.forms ?? []).map((row) => withOverlay(decodedForm(row)));
  }, [formsQuery.data, stamp]);
  const formsById = useMemo(() => new Map(forms.map((form) => [form.formId, form])), [forms]);
  const submissions = useMemo(
    () => (issueId ? (fillingsQuery.data?.issue?.formSubmissions ?? []).map(submissionFromRow) : []),
    [fillingsQuery.data, issueId],
  );

  const createForm = useCallback(
    async (name?: string): Promise<Form | null> => {
      try {
        const { data } = await client.mutate({
          mutation: CreateFormDocument,
          refetchQueries: ["Forms"],
          variables: { input: { name: name ?? null } },
        });
        return data?.createForm ? formFromRow(data.createForm) : null;
      } catch (err) {
        toastMutationError(err, "Failed to create form");
        return null;
      }
    },
    [client],
  );

  const updateForm = useCallback(
    async (
      formId: string,
      patch: Partial<Pick<Form, "name" | "description" | "bannerPreset" | "bannerMediaId" | "requiredByDefault" | "sharedWith">>,
    ): Promise<void> => {
      const { sharedWith, ...identity } = patch;
      try {
        if (Object.keys(identity).length > 0) {
          const { data } = await client.mutate({ mutation: UpdateFormDocument, variables: { id: formId, input: identity } });
          const result = data?.updateForm;
          if (result?.__typename !== "Form") toastManager.add({ title: result?.message ?? "Failed to update form", type: "error" });
        }
        if (sharedWith) {
          const { data } = await client.mutate({
            mutation: SetFormSharesDocument,
            variables: { id: formId, shares: sharedWith.map(({ id, kind }) => ({ id, kind })) },
          });
          const result = data?.setFormShares;
          if (result?.__typename !== "Form") toastManager.add({ title: result?.message ?? "Failed to share form", type: "error" });
        }
      } catch (err) {
        toastMutationError(err, "Failed to update form");
      }
    },
    [client],
  );

  const patchShape = useCallback(
    (formId: string, patch: (shape: FormShape) => FormShape) => {
      const row = cachedRow(client, formId);
      if (!row) return;
      stageShape(client, formId, patch(overlays.get(formId) ?? editableShape(decodedForm(row))));
    },
    [client],
  );

  const publishForm = useCallback(
    async (formId: string): Promise<FormOutcome> => {
      const expectedVersion = await flushFormSaves(client, formId);
      if (expectedVersion === null) return failure({ code: "NOT_FOUND", message: "Form not found" });
      if (expectedVersion === false) return failure({ message: "Your latest edits aren't saved yet, so it can't be published." });
      try {
        const { data } = await client.mutate({
          mutation: PublishFormDocument,
          variables: { expectedVersion, id: formId, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone },
        });
        return formOutcome(data?.publishForm);
      } catch {
        return failure({ message: "Failed to publish" });
      }
    },
    [client],
  );

  const simple = useCallback(
    async (
      formId: string,
      run: () => Promise<{ __typename?: string } | null | undefined>,
      message: string,
      flush: () => Promise<unknown> = () => flushFormSaves(client, formId),
    ): Promise<FormOutcome> => {
      if ((await flush()) === false) {
        const unsaved = failure({ message: "Your latest edits aren't saved yet. Try again in a moment." });
        toastManager.add({ title: unsaved.message, type: "error" });
        return unsaved;
      }
      try {
        const outcome = formOutcome(await run());
        if (!outcome.ok) toastManager.add({ title: outcome.message, type: "error" });
        return outcome;
      } catch (err) {
        toastMutationError(err, message);
        return failure(null);
      }
    },
    [client],
  );

  const discardDraft = useCallback(
    async (formId: string) => {
      const unsent = await shapeSaves.takeBack(formId);
      const outcome = await simple(
        formId,
        async () => (await client.mutate({ mutation: DiscardFormDraftDocument, variables: { id: formId } })).data?.discardFormDraft,
        "Failed to discard changes",
        () => settingsSaves.flush(formId),
      );
      if (outcome.ok) {
        shapeVersions.delete(formId);
        setOverlay(formId, null);
      } else if (unsent) {
        stageShape(client, formId, unsent);
      }
      return outcome;
    },
    [client, simple],
  );
  const restoreDraft = useCallback(
    (formId: string, draft: FormShape) => stageShape(client, formId, draft),
    [client],
  );
  const unpublishForm = useCallback(
    (formId: string) =>
      simple(
        formId,
        async () => (await client.mutate({ mutation: UnpublishFormDocument, variables: { id: formId } })).data?.unpublishForm,
        "Failed to unpublish form",
      ),
    [client, simple],
  );
  const reopenForm = useCallback(
    (formId: string) =>
      simple(
        formId,
        async () => (await client.mutate({ mutation: ReopenFormDocument, variables: { id: formId } })).data?.reopenForm,
        "Failed to reopen form",
      ),
    [client, simple],
  );
  const duplicateForm = useCallback(
    (formId: string) =>
      simple(
        formId,
        async () =>
          (await client.mutate({ mutation: DuplicateFormDocument, refetchQueries: ["Forms"], variables: { id: formId } })).data
            ?.duplicateForm,
        "Failed to duplicate form",
      ),
    [client, simple],
  );

  const deleteForm = useCallback(
    async (formId: string): Promise<boolean> => {
      shapeSaves.drop(formId);
      settingsSaves.drop(formId);
      try {
        const { data } = await client.mutate({ mutation: DeleteFormDocument, variables: { id: formId } });
        if (data?.deleteForm) client.cache.evict({ id: formCacheId(client, formId) });
        client.cache.gc();
        return !!data?.deleteForm;
      } catch (err) {
        toastMutationError(err, "Failed to delete form");
        return false;
      }
    },
    [client],
  );

  const addItem = useCallback(
    (formId: string, item: FormItem, index?: number) =>
      patchShape(formId, (shape) => {
        const items = [...shape.items];
        const at = index ?? items.length;
        items.splice(item.kind === "section" ? rootSeatAt(items, at) : at, 0, item);
        return { ...shape, items };
      }),
    [patchShape],
  );
  const updateItem = useCallback(
    (formId: string, itemId: string, patch: (item: FormItem) => FormItem) =>
      patchShape(formId, (shape) => ({ ...shape, items: shape.items.map((item) => (item.itemId === itemId ? patch(item) : item)) })),
    [patchShape],
  );
  const removeItem = useCallback(
    (formId: string, itemId: string) =>
      patchShape(formId, (shape) => ({ ...shape, items: withItemRemoved(shape.items, itemId) })),
    [patchShape],
  );
  const moveItem = useCallback(
    (formId: string, itemId: string, index: number, patch?: (item: FormItem) => FormItem) =>
      patchShape(formId, (shape) => ({ ...shape, items: withRunMoved(shape.items, itemId, index, patch) })),
    [patchShape],
  );
  const reorderItems = useCallback(
    (formId: string, activeId: string, overId: string) => {
      if (activeId === overId) return;
      patchShape(formId, (shape) => ({ ...shape, items: reorderWithRun(shape.items, activeId, overId) }));
    },
    [patchShape],
  );
  const releaseItem = useCallback(
    (formId: string, itemId: string) => patchShape(formId, (shape) => ({ ...shape, items: withConditionReleased(shape.items, itemId) })),
    [patchShape],
  );
  const replaceItem = useCallback(
    (formId: string, itemId: string, next: FormItem, keep?: boolean) =>
      patchShape(formId, (shape) => ({ ...shape, items: withItemReplaced(shape.items, itemId, next, keep) })),
    [patchShape],
  );
  const relinkItem = useCallback(
    (
      formId: string,
      itemId: string,
      relink: (item: FormFieldItem) => FormItem,
      customFieldsById: ReadonlyMap<string, CustomFieldDef>,
    ) =>
      patchShape(formId, (shape) => {
        const current = shape.items.find((item) => item.itemId === itemId);
        if (!current || !isFieldItem(current)) return shape;
        const next = relink(current);
        return {
          ...shape,
          items: withItemRelinked(
            shape.items,
            itemId,
            next,
            resolveItemField(current, customFieldsById),
            isFieldItem(next) ? resolveItemField(next, customFieldsById) : null,
          ).items,
        };
      }),
    [patchShape],
  );
  const setShape = useCallback((formId: string, shape: FormShape) => patchShape(formId, () => shape), [patchShape]);
  const moveSection = useCallback(
    (formId: string, sectionItemId: string, by: -1 | 1) =>
      patchShape(formId, (shape) => ({ ...shape, items: withSectionMoved(shape.items, sectionItemId, by) })),
    [patchShape],
  );
  const updateSettings = useCallback(
    (formId: string, patch: (settings: FormSettings) => FormSettings) => {
      const row = cachedRow(client, formId);
      if (!row) return;
      stageSettings(client, formId, patch(settingsOverlays.get(formId) ?? decodedForm(row).settings ?? {}));
    },
    [client],
  );

  const saveSubmission = useCallback(
    (
      filling: FormSubmission,
      onSaved?: (stored: Record<string, unknown>) => void,
      changed: readonly string[] | null = null,
      onRefused?: (message: string) => void,
    ) => answerSaves.stage(client, filling.submissionId, { changed, filling, onRefused: onRefused ?? null, onSaved: onSaved ?? null }),
    [client],
  );

  const submit = useCallback(
    async (
      filling: FormSubmission,
      changed: readonly string[] | null = null,
    ): Promise<
      | { ok: true; submission: FormSubmission; failedWrites: string[] }
      | { ok: false; code: string; message: string; errors?: Record<string, string> }
    > => {
      // A refused Submit puts the unsent autosave back, so the last answer is not lost.
      const unsent = await answerSaves.takeBack(filling.submissionId);
      const refused = () => {
        if (unsent) answerSaves.stage(client, filling.submissionId, unsent);
      };
      try {
        const { data } = await client.mutate({
          mutation: SubmitFormSubmissionDocument,
          refetchQueries: [...FILLING_QUERIES, "IssueViewData", "ActivityViewData"],
          variables: {
            input: { ...fillingInput(filling, changed), timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone },
          },
        });
        const result = data?.submitFormSubmission;
        if (result?.__typename === "FormSubmitted") {
          return { failedWrites: result.failedWrites, ok: true, submission: submissionFromRow(result.submission) };
        }
        refused();
        if (result?.__typename === "FormSubmitInvalid") {
          return { code: result.code, errors: result.errors as Record<string, string>, message: result.message, ok: false };
        }
        return { code: result?.code ?? "FAILED", message: result?.message ?? "Failed to submit", ok: false };
      } catch (err) {
        refused();
        toastMutationError(err, "Failed to submit");
        return { code: "FAILED", message: "Failed to submit", ok: false };
      }
    },
    [client],
  );

  const deleteSubmission = useCallback(
    async (submissionId: string): Promise<{ deleted: boolean; undoable: boolean }> => {
      const unsent = await answerSaves.takeBack(submissionId);
      try {
        const { data } = await client.mutate({
          mutation: DeleteFormSubmissionDocument,
          refetchQueries: [...FILLING_QUERIES, "IssueViewData"],
          variables: { id: submissionId },
        });
        const result = data?.deleteFormSubmission;
        if (result?.__typename === "FormSubmissionDeleted") return { deleted: true, undoable: result.undoable };
        toastManager.add({ title: result?.message ?? "Failed to delete", type: "error" });
      } catch (err) {
        toastMutationError(err, "Failed to delete");
      }
      if (unsent) answerSaves.stage(client, submissionId, unsent);
      return { deleted: false, undoable: false };
    },
    [client],
  );

  const restoreSubmission = useCallback(
    async (submissionId: string) => {
      try {
        const { data } = await client.mutate({
          mutation: RestoreFormSubmissionDocument,
          refetchQueries: FILLING_QUERIES,
          variables: { id: submissionId },
        });
        const result = data?.restoreFormSubmission;
        if (result?.__typename !== "FormSubmission") {
          toastManager.add({
            title: result?.code === "SUBMISSION_LIMIT_REACHED" ? "Submission limit reached" : (result?.message ?? "Failed to restore"),
            type: "error",
          });
        }
      } catch (err) {
        toastMutationError(err, "Failed to restore");
      }
    },
    [client],
  );

  const setSubmissionShares = useCallback(
    async (submissionId: string, sharedWith: FormShare[]) => {
      try {
        const { data } = await client.mutate({
          mutation: SetFormSubmissionSharesDocument,
          variables: { id: submissionId, shares: sharedWith.map(({ id, kind }) => ({ id, kind })) },
        });
        const result = data?.setFormSubmissionShares;
        if (result?.__typename !== "FormSubmission") toastManager.add({ title: result?.message ?? "Failed to share", type: "error" });
      } catch (err) {
        toastMutationError(err, "Failed to share");
      }
    },
    [client],
  );

  const removeFormPhotos = useRemoveFormPhotos();

  const formsLoading = formsQuery.loading && !formsQuery.data;
  const formsFailed = !!formsQuery.error && !formsQuery.data;
  const { refetch: refetchFormsQuery } = formsQuery;
  const refetchForms = useCallback(() => void refetchFormsQuery().catch(() => undefined), [refetchFormsQuery]);
  const submissionsLoading = !!issueId && fillingsQuery.loading && !fillingsQuery.data;
  return useMemo(
    () => ({
      addItem,
      createForm,
      deleteForm,
      deleteSubmission,
      discardDraft,
      duplicateForm,
      forms,
      formsById,
      formsFailed,
      formsLoading,
      moveItem,
      moveSection,
      publishForm,
      refetchForms,
      relinkItem,
      releaseItem,
      removeFormPhotos,
      removeItem,
      reopenForm,
      reorderItems,
      replaceItem,
      restoreDraft,
      restoreSubmission,
      saveSubmission,
      setShape,
      setSubmissionShares,
      submissions,
      submissionsLoading,
      submit,
      unpublishForm,
      updateForm,
      updateItem,
      updateSettings,
    }),
    [
      addItem,
      createForm,
      deleteForm,
      deleteSubmission,
      discardDraft,
      duplicateForm,
      forms,
      formsById,
      formsFailed,
      formsLoading,
      moveItem,
      moveSection,
      publishForm,
      refetchForms,
      relinkItem,
      releaseItem,
      removeFormPhotos,
      removeItem,
      reopenForm,
      reorderItems,
      replaceItem,
      restoreDraft,
      restoreSubmission,
      saveSubmission,
      setShape,
      setSubmissionShares,
      submissions,
      submissionsLoading,
      submit,
      unpublishForm,
      updateForm,
      updateItem,
      updateSettings,
    ],
  );
}

export type FormsStore = ReturnType<typeof useFormsStore>;
